import { useCallback, useMemo } from "react";

import {
  faExclamationCircle,
  faSearch,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";
import { AxiosError } from "axios";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { isEmpty } from "lodash-es";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { FilterFormData } from "src/base-components/FilterPopover/types";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { TableComp } from "src/base-components/Table";
import { EventFilterRequestEnvironmentEnum } from "src/clients/features-retrieve";
import { EmptyState } from "src/design-system/EmptyState";
import { useEntitySchemas } from "src/entities/queries";
import {
  EventsEllipsisSwitchEnvironment,
  EventsSubHeader,
} from "src/eventsCatalogue/EventsSubHeader";
import { getFiltersUrlParamKey } from "src/eventsCatalogue/hooks/useEventFiltersUrl";
import {
  DeserializedEvent,
  useEvent,
  useEventSchema,
  useEventSchemas,
  useEventsFilters,
  useFilteredEventsQuery,
} from "src/eventsCatalogue/queries";
import { getColumns } from "src/eventsCatalogue/tableConfig";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EventsCatalogueParams, getUrlToEventsPage } from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const EVENT_SEARCH_PARAM_KEY = "q";
const TIMEOUT_ERROR_MESSAGE =
  "The search could not be completed because the time window is too large. Consider selecting a narrower time window.";
export const TIME_WINDOW_SESSION_STORAGE_KEY = "events-time-window";

export const EventsCatalogue = () => {
  const [environment] = useEnvironment();
  const context = useWorkspaceContext();
  const {
    event_id: selectedEventId,
    event_type,
    orgId,
    wsId,
  } = useParamsDecode<EventsCatalogueParams>();
  const { workspace } = useWorkspaceContext();
  const { data: eventTypes } = useEventSchemas();
  const { data: schema, isLoading: isLoadingSchema } =
    useEventSchema(event_type);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });
  const currentEvent = eventTypes?.find(
    (event) => event.event_type === event_type,
  );

  useDocumentTitle(
    currentEvent ? [currentEvent.display_name_plural, "Events"] : ["Events"],
  );

  const filterUrlKeys = getFiltersUrlParamKey(event_type);

  const { onDateRangePickerChange, timeWindow } = useTimeWindow(
    {
      from: new Date(
        searchParams.get(filterUrlKeys.from) ??
          subDays(startOfToday(), 7).toISOString(),
      ),
      to: new Date(
        searchParams.get(filterUrlKeys.to) ?? endOfToday().toISOString(),
      ),
    },
    TIME_WINDOW_SESSION_STORAGE_KEY,
  );

  const onTimeWindowChange: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    onDateRangePickerChange(range);

    // Sync date range to URL (without changing filters)
    applyFilters(undefined, range);

    const currentPath = window.location.pathname;
    if (currentPath.includes("/event/")) {
      navigate(
        getUrlToEventsPage({
          orgId,
          wsId,
          eventType: event_type,
        }),
      );
    }
  };

  const searchQuery = searchParams.get(EVENT_SEARCH_PARAM_KEY);

  const {
    applyFilters,
    clearFilters,
    urlFilterParams,
    filterParams,
    hasFilterCondition,
  } = useEventsFilters({
    eventType: event_type,
    endTimestamp: timeWindow.to.toISOString(),
    environment: environment as EventFilterRequestEnvironmentEnum,
    startTimestamp: timeWindow.from.toISOString(),
  });

  const handleApplyFilters = useCallback(
    (formData: FilterFormData) => {
      applyFilters(formData, timeWindow);
    },
    [applyFilters, timeWindow],
  );

  const {
    events,
    isPending: isFilterPending,
    error: filterError,
    handleFetchNextPage,
    isFetchingNextPage,
    refetch: refreshEvents,
    isRefetching: isRefetchingEvents,
  } = useFilteredEventsQuery({
    eventType: event_type,
    environment: environment as EventFilterRequestEnvironmentEnum,
    filterParams,
    enabled: !searchQuery,
  });
  const {
    data: event,
    isFetching: isEventFetching,
    isLoading: isEventLoading,
    error: eventError,
  } = useEvent(
    {
      eventType: event_type,
      eventId: searchQuery ?? "",
      environment,
      timeWindow: {
        from: timeWindow.from.toISOString(),
        to: timeWindow.to.toISOString(),
      },
    },
    {
      retry: (failureCount, error) => error.status !== 404 && failureCount < 3,
    },
  );

  const memoizedColumns = useMemo(() => {
    return schema ? getColumns(schema, environment, entitiesSchemasData) : [];
  }, [schema, entitiesSchemasData, environment]);

  const handleViewClick = (row: DeserializedEvent) => {
    navigate(
      getUrlToEventsPage({
        orgId,
        wsId,
        eventType: event_type,
        eventId:
          selectedEventId === row.payload.id ? undefined : row.payload.id,
      }),
    );
  };

  const mappedEvents = useMemo(() => {
    if (searchQuery?.length && event) {
      return [event];
    }
    return (events as DeserializedEvent[]) ?? [];
  }, [searchQuery, event, events]);

  const onSearchQueryChange = (query: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (query.length) {
        newParams.set(EVENT_SEARCH_PARAM_KEY, query);
      } else {
        newParams.delete(EVENT_SEARCH_PARAM_KEY);
      }
      return newParams;
    });
  };

  const isTableLoading =
    isLoadingSchema || (searchQuery ? isEventFetching : isFilterPending);
  const hasTimeoutError =
    filterError?.status === 408 ||
    (eventError as AxiosError)?.response?.status === 408;

  const renderEventsContent = () => {
    if (hasTimeoutError) {
      return (
        <EmptyState
          dataLoc="events-timeout-state"
          description={TIMEOUT_ERROR_MESSAGE}
          headline="Search Timeout"
          icon={faSearch}
        />
      );
    }

    // Show filter error if present (including 400 errors from POST request)
    if (filterError && (!isFilterPending || filterError.status === 400)) {
      const error = filterError.data
        ? errorMessage(filterError?.data)
        : "There was an error loading events. Please try again.";
      return (
        <EmptyState
          dataLoc="events-filter-error-state"
          description={error}
          headline="Error loading events"
          icon={faExclamationCircle}
          variant="error"
        />
      );
    }

    if (
      (!isFilterPending && isEmpty(mappedEvents)) ||
      (searchQuery?.length && !isEventLoading && !event)
    ) {
      return (
        <EmptyState
          actionButton={
            hasFilterCondition
              ? {
                  label: "Clear filters",
                  onClick: clearFilters,
                  icon: faTrashAlt,
                }
              : undefined
          }
          dataLoc="events-empty-state"
          description={
            hasFilterCondition
              ? "No data found for the selected filters. Adjust the filters, and try running the query again."
              : "No events found in the given time window. Check the ID or adjust the time window"
          }
          headline="No events found"
          icon={faSearch}
        />
      );
    }

    return (
      <div className="relative flex h-full w-full flex-col p-4">
        <TableComp
          columns={memoizedColumns}
          data={mappedEvents}
          dataLoc="events-table"
          frameClassName="w-full"
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isTableLoading}
          rowPropsGetter={(row) => ({
            onClick: () => handleViewClick(row.original),
            classNameOverrides: twJoin(
              row.original.payload.id === selectedEventId
                ? "selected bg-gray-50"
                : "hover:bg-gray-50",
              "cursor-pointer",
            ),
          })}
          uiStoreKey={getTableStoreKey(event_type)}
          variant="compact"
          onScrolledToTheBottom={handleFetchNextPage}
        />
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Events"
        sideContent={<EventsEllipsisSwitchEnvironment />}
      />
      <EventsSubHeader
        dateRangePickerValue={timeWindow}
        eventTypes={eventTypes ?? []}
        hasFilterCondition={hasFilterCondition}
        isRefreshing={isRefetchingEvents}
        searchQuery={searchQuery}
        urlFilterParams={urlFilterParams}
        onApplyFilters={handleApplyFilters}
        onRefresh={refreshEvents}
        onSearchQueryChange={onSearchQueryChange}
        onTimeWindowChange={onTimeWindowChange}
      />
      <div className="flex min-h-0 flex-1">
        <FloatingWindowsProvider>
          <div className="flex w-full shrink flex-col overflow-x-auto p-5">
            <WhitePane noMinWidth>
              <div className="flex h-full w-full flex-col">
                {renderEventsContent()}
              </div>
            </WhitePane>
          </div>
          <Outlet context={context} />
        </FloatingWindowsProvider>
      </div>
    </div>
  );
};

export const getTableStoreKey = (eventType: string) => `events-${eventType}`;
